/**
 * One number format for the dashboard and the monthly report (`en-GB`, house rules).
 *
 * - Counts: `2,687`.
 * - Shares arrive as fractions (0 to 1) and print as whole per cent with no space (`54%`).
 *   One decimal below 10 % (`5.4%`), or when the figure is read against a bar such as the
 *   99.5 % go-live bar (`96.2%`). A tiny part that rounds to 0 prints `<1%`.
 * - Changes: `+3 pts` for shares, `+4%` for counts, always with a sign.
 * - Time: `4 min`, `4.1 h`, `2.5 days`.
 * - Money: compact per currency (`€2M`, `€2.1M`, `$620K`, `DKK 1.2M`), one decimal at most
 *   and no trailing `.0`, like the shares above (`5%`, never `5.0%`).
 *
 * Every function returns a plain string, so a figure looks the same in a tile, a table,
 * a chart tooltip and the printed report. Unknown values print as `Not measured yet`
 * only where the caller asks for it; the helpers return `null` so the caller can say why.
 */

const LOCALE = "en-GB";

const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });
const oneDecimal = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function finite(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** `2,687`; `0` for an unknown count. */
export function formatCount(value: number | null | undefined): string {
  return integer.format(finite(value) ? Math.round(value) : 0);
}

/** "1 quotation" / "432 quotations" (the plural is `${noun}s` unless given). */
export function countNoun(value: number, noun: string, plural = `${noun}s`): string {
  return `${formatCount(value)} ${Math.round(value) === 1 ? noun : plural}`;
}

export interface PercentOptions {
  /** One decimal always, for a figure read against a bar (99.5 %). */
  precise?: boolean;
}

/**
 * A fraction as per cent: `54%`, `5.4%` (below 10 %), `96.2%` with `precise`, `<1%` for a
 * small part that would round to 0, `null` when unknown.
 */
export function formatPercent(
  share: number | null | undefined,
  options: PercentOptions = {},
): string | null {
  if (!finite(share)) {
    return null;
  }
  const pct = share * 100;
  if (pct > 0 && pct < 0.05) {
    return "<1%";
  }
  if (options.precise || (pct > 0 && pct < 10)) {
    const text = oneDecimal.format(pct);
    return `${text.endsWith(".0") && !options.precise ? text.slice(0, -2) : text}%`;
  }
  return `${integer.format(Math.round(pct))}%`;
}

/** `part` of `whole` as per cent (same rules), `null` without a base. */
export function formatShareOf(
  part: number,
  whole: number,
  options?: PercentOptions,
): string | null {
  return whole > 0 ? formatPercent(part / whole, options) : null;
}

/** "14 of 17". */
export function formatXofY(part: number, whole: number): string {
  return `${formatCount(part)} of ${formatCount(whole)}`;
}

export type DeltaDirection = "up" | "down" | "flat";

export interface Delta {
  /** "+3 pts", "-2 pts", "+4%", "No change". */
  text: string;
  direction: DeltaDirection;
  /** The change in points (shares) or per cent (counts); 0 when flat. */
  size: number;
}

/**
 * The change of a share between two periods in percentage points: `+3 pts`. Under half a
 * point reads "No change". `null` when either side is unknown.
 */
export function shareDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): Delta | null {
  if (!finite(current) || !finite(previous)) {
    return null;
  }
  const points = (current - previous) * 100;
  const rounded = Math.round(points);
  if (rounded === 0) {
    return { text: "No change", direction: "flat", size: 0 };
  }
  return {
    text: `${rounded > 0 ? "+" : "-"}${Math.abs(rounded)} ${Math.abs(rounded) === 1 ? "pt" : "pts"}`,
    direction: rounded > 0 ? "up" : "down",
    size: points,
  };
}

/** The change of a count in per cent: `+4%`. `null` without a previous count. */
export function countDelta(
  current: number | null | undefined,
  previous: number | null | undefined,
): Delta | null {
  if (!finite(current) || !finite(previous) || previous <= 0) {
    return null;
  }
  const pct = ((current - previous) / previous) * 100;
  const rounded = Math.round(pct);
  if (rounded === 0) {
    return { text: "No change", direction: "flat", size: 0 };
  }
  return {
    text: `${rounded > 0 ? "+" : "-"}${integer.format(Math.abs(rounded))}%`,
    direction: rounded > 0 ? "up" : "down",
    size: pct,
  };
}

/** Minutes as `4 min`, `4.1 h` (from 90 minutes), `2.5 days` (from 36 hours); `null` unknown. */
export function formatDuration(minutes: number | null | undefined): string | null {
  if (!finite(minutes) || minutes < 0) {
    return null;
  }
  if (minutes < 1) {
    return "under 1 min";
  }
  if (minutes < 90) {
    return `${integer.format(Math.round(minutes))} min`;
  }
  const hours = minutes / 60;
  if (hours < 36) {
    return `${oneDecimal.format(hours)} h`;
  }
  return `${oneDecimal.format(hours / 24)} days`;
}

/** Hours, same rules as {@link formatDuration}. */
export function formatHours(hours: number | null | undefined): string | null {
  return finite(hours) ? formatDuration(hours * 60) : null;
}

// DS-GAP(compact-currency-icu): formatCompactCurrency leaves the minimum fraction digits to
// the runtime, so this is a local copy with `minimumFractionDigits: 0`.
/** Nordic crowns print their code (`DKK 1.2M`), as the design system does; others a symbol. */
const CODE_CURRENCIES = new Set(["DKK", "SEK", "NOK"]);
const compactMoney = new Map<string, Intl.NumberFormat>();

/**
 * Compact money in one currency: `€2M`, `€2.1M`, `$620K`, `DKK 1.2M`. Never summed across
 * currencies.
 *
 * Same shape as the design system's `formatCompactCurrency`, plus `minimumFractionDigits: 0`:
 * without it the trailing `.0` depends on the ICU version of the runtime (Node 22 prints
 * `€2.0M` and `$620.0K`, Node 26 `€2M`), so the figure would change with the browser.
 */
export function formatMoney(value: number | null | undefined, currency: string): string {
  const code = currency.toUpperCase();
  let format = compactMoney.get(code);
  if (!format) {
    format = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: CODE_CURRENCIES.has(code) ? "code" : "narrowSymbol",
      notation: "compact",
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
    compactMoney.set(code, format);
  }
  return format.format(finite(value) ? value : 0);
}

/** Short month names fixed in the house style ("Sep", never "Sept"). */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const MONTH_YEAR = new Intl.DateTimeFormat(LOCALE, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const TIME = new Intl.DateTimeFormat(LOCALE, { hour: "2-digit", minute: "2-digit" });

function parseDay(iso: string): Date | null {
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "4 Oct" from an ISO date. */
export function formatDayMonth(iso: string): string {
  const date = parseDay(iso);
  return date ? `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}` : iso;
}

/** "4 Oct 2026" from an ISO date. */
export function formatDate(iso: string): string {
  const date = parseDay(iso);
  return date ? `${formatDayMonth(iso)} ${date.getUTCFullYear()}` : iso;
}

/** "September 2026" from `2026-09`. */
export function formatMonth(month: string): string {
  const date = parseDay(`${month}-01`);
  return date ? MONTH_YEAR.format(date) : month;
}

/** "08:40" in the viewer's time zone, for "As of 08:40". */
export function formatClock(iso: string): string {
  const date = parseDay(iso);
  return date ? TIME.format(date) : iso;
}

/**
 * The last day counted, from a period whose `end` is the day AFTER it (exclusive end):
 * "4 Oct" for `end` 2026-10-05.
 */
export function lastDayOf(endExclusive: string): string {
  const date = parseDay(endExclusive);
  if (!date) {
    return endExclusive;
  }
  return new Date(date.getTime() - 86_400_000).toISOString().slice(0, 10);
}

/** "29 Sep to 5 Oct" for a period `[start, end)`. */
export function formatRange(start: string, endExclusive: string): string {
  return `${formatDayMonth(start)} to ${formatDayMonth(lastDayOf(endExclusive))}`;
}

/**
 * A date and time for a mail or an event, in the viewer's language with the month as a
 * word ("7 Oct 2026, 17:31"), so day and month are never mixed up. A 24-hour clock
 * except for a US locale. Empty for a missing value, the input for an unreadable one
 * (360 simulation P39: one helper instead of mixed locales and clocks).
 */
export function formatDateTime(
  value: string | null | undefined,
  locale: string | undefined = undefined,
): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const resolved = locale ?? Intl.DateTimeFormat().resolvedOptions().locale;
  return new Intl.DateTimeFormat(resolved, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: usesTwelveHours(resolved) ? "h12" : "h23",
  }).format(date);
}

/** True for a US locale: the only one that keeps the 12-hour clock. */
export function usesTwelveHours(locale: string): boolean {
  return /^en-US\b/i.test(locale);
}
